// Self-contained anti-cheat module. No React imports.
//
// Spec: docs/architecture/infrastructure.md "Anti-cheat module" section
// + docs/architecture/data-model.md "event" table.

import type { AntiCheatEvent, AntiCheatPayload } from "./events";
import { probeDisplayPlacement, probeFormFactor } from "./displays";
// FEAT-38 — the pure interaction fold (counts only, never a key or a point).
import {
  createInteractionFold,
  equalCounts,
  isZeroCounts,
  maxCounts,
  ZERO_COUNTS,
  type InteractionCounts,
  type InteractionFold,
} from "./interaction";
import { describeKeyEvent, matchBlockedShortcut } from "./shortcuts";
import { OWN_TESTS_ATTR } from "./workArea"; // FEAT-29

export type { AntiCheatEvent, AntiCheatPayload } from "./events";

export interface AntiCheatController {
  dispose(): void;
  flushNow(): Promise<void>;
  onQuestionChange(position: number): void;
  emit(payload: AntiCheatPayload): void;
  // EF-11: the OS file dialog blurs the window (and may drop fullscreen), which
  // is the task, not a tab switch. While a picker is open the controller
  // DROPS tab_blur / tab_focus / cursor_left_window / fullscreen_exit and has
  // emitted `file_picker_opened` instead. Returns the (idempotent) end
  // function; the window also ends itself on focus + 1 s and at 120 s.
  beginFilePicker(position?: number): () => void;
  // True exactly while that window is open, i.e. while `emit` drops the four
  // events above. The fullscreen overlay reads it at the moment of an exit to
  // tell the candidate "not counted against you" — and only then.
  isFilePickerActive(): boolean;
}

export interface AntiCheatOptions {
  // Candidate token — the event route is now token-bound (/api/t/{token}/events)
  // so the server can verify the attempt belongs to the token's test (F2).
  token: string;
  attemptId: string;
  flushUrl?: string;
  batchMs?: number;
  getQuestionId?: () => string | undefined;
  // Observer for the candidate-facing "Copy & pasting detected" notice. Called
  // AFTER the paste event has been recorded and the native dispatch has fully
  // completed (see recordPastedText) with the exact payload that was emitted,
  // for clipboard pastes and drops alike. Purely additive: it never blocks the
  // paste, never changes what is recorded, and a throwing observer is
  // swallowed so UI errors can never cost the recruiter an event. The module
  // stays React-free — the React bridge lives in ./react/useAntiCheat.ts.
  onPasteRecorded?: (payload: PastePayload) => void;
  // EF-8: observer of every payload that was actually queued (after the
  // file-picker suppression), so the camera monitor can add a photo on a
  // fullscreen exit or a tab return. Same contract as onPasteRecorded: never
  // blocks, never changes what is recorded, a throwing observer is swallowed.
  onEvent?: (payload: AntiCheatPayload) => void;
  // FEAT-25: false when the recruiter switched activity monitoring off for
  // this test. The controller then installs NO detector (tab, paste, drop,
  // shortcuts, network, cursor, second screen) and blocks nothing — but it
  // still exists: `question_viewed` drives the server's resume pointer, the
  // flush pipeline carries the fullscreen and camera events, and the
  // file-picker window still mutes the fullscreen exit a file dialog causes.
  // Default true. The server drops these events anyway; this is what keeps
  // the page from blocking a paste nobody asked it to block.
  detectors?: boolean;
  // FEAT-38: true when /preview said interaction context is available for
  // this attempt (the platform flag ∧ the legal gate; the Gate caches it in
  // the candidate meta). The controller then folds mouse / key / wheel
  // TIMING into per-attempt COUNTS (./interaction.ts) and ships one
  // `interaction_summary` on the cadence, on dispose and on pagehide. Needs
  // `detectors` too — it is an activity-monitoring signal, and the server
  // drops it under that switch. Default false. Context, never a flag.
  interaction?: boolean;
  // Runs FIRST at pagehide, before the final beacon, while `emit` is still
  // live: the seam for state held outside the controller that only a
  // teardown would otherwise flush. On a real tab close no React cleanup
  // runs, so the camera monitor's stop() never comes — and the monitor is
  // created after the controller (it waits on the stream), so a pagehide
  // listener of its own would fire after this beacon had gone. A throwing
  // hook is swallowed: it must never cost the beacon.
  onPageHide?: () => void;
}

export type PastePayload = Extract<AntiCheatPayload, { type: "paste" }>;

// EF-11 — the payloads a native file dialog produces as side effects.
const FILE_PICKER_SUPPRESSED = new Set<AntiCheatPayload["type"]>([
  "tab_blur",
  "tab_focus",
  "cursor_left_window",
  "fullscreen_exit",
]);
// The dialog's own blur/focus lands within a frame of the input's change /
// cancel; a second of grace covers every browser's ordering.
const FILE_PICKER_GRACE_MS = 1000;
// A picker nobody closed (a crashed dialog) must not suppress for ever.
const FILE_PICKER_MAX_MS = 120_000;

const DEFAULT_BATCH_MS = 2000;
// The server's per-request cap (`EventBatch.events`, max_length=200, in
// backend/api/events.py): a longer batch 422s, so every send is cut to it.
const MAX_EVENTS_PER_REQUEST = 200;
// How long a disposed controller's queue waits for the next controller of the
// same attempt to adopt it (see `parkEvents`) before it is sent on its own.
const HANDOFF_MS = 3000;
const VISIBILITY_DEDUPE_MS = 100;
const EDITOR_SELECTOR = ".monaco-editor";
// B2 — a pointer excursion off the viewport must last at least this long to
// count. Filters benign scrollbar / OS-taskbar / menu-bar reaches; catches a
// genuine "glanced at a second monitor" excursion.
const CURSOR_OUT_MIN_MS = 1500;
// B2/R3 — the ARM-TIME backdated excursion (pointer already outside when the
// controller mounted, see the arming blind spot below) is genuinely ambiguous:
// a dual-monitor dev whose pointer simply RESTS on the other screen looks
// identical to a real look-away, and its duration = "time until they first move
// the mouse back", which is not a reliable cheat measure. Cap only that one
// excursion's reported out_ms so it can't ALONE cross the server's cumulative
// flag threshold (risk.CURSOR_OUT_FLAG_MS = 30_000) — it still surfaces in the
// timeline and adds to the cumulative, but now needs corroboration to flag.
// This is the exact resting-pointer false positive R3 set out to avoid.
const CURSOR_ARM_EXCURSION_CAP_MS = 10_000;
// R1 — positional second-screen fallback (Firefox/Safari, no screen.isExtended).
// Flag only when MORE than this fraction of the window sits off the primary
// screen, so a single-monitor window merely dragged/nudged past an edge no
// longer fakes a second display. A window genuinely relocated to another monitor
// reads ~1.0 (a whole axis past the edge), so a high bar keeps every true
// positive while killing the accidental-drag false positive. One-line tunable —
// raise toward 0.75 if even a half-off window should not flag.
const SECOND_SCREEN_OFFSCREEN_FRACTION = 0.5;

// FEAT-29 — the candidate's own test cases sit next to the code editor and are
// part of the same work: a paste, a drop and the clipboard shortcuts are
// treated there exactly as in the editor (the paste LANDS, is recorded as a
// paste in the editor, and flags like one). Marked by this attribute on the
// fields' wrapper. Not the context menu: Monaco draws its own, a text field
// would open the browser's (see contentProtection.ts), so that rule keeps
// asking `inEditor`.
export { OWN_TESTS_ATTR };
const WORK_AREA_SELECTOR = `${EDITOR_SELECTOR}, [${OWN_TESTS_ATTR}]`;

function within(e: Event, selector: string): boolean {
  const path = typeof (e as Event & { composedPath?: () => EventTarget[] }).composedPath === "function"
    ? (e as Event & { composedPath: () => EventTarget[] }).composedPath()
    : [];
  for (const node of path) {
    if (node instanceof Element && node.closest(selector)) return true;
  }
  if (e.target instanceof Element && e.target.closest(selector)) return true;
  // Paste-event fallback: when paste fires from a keystroke while the editor's
  // textarea has focus, the event target may be the document itself depending
  // on browser. For other event types we trust target/composedPath.
  if (e.type === "paste") {
    const active = document.activeElement;
    if (active instanceof Element && active.closest(selector)) return true;
  }
  return false;
}

export function inEditor(e: Event): boolean {
  return within(e, EDITOR_SELECTOR);
}

/** The code editor OR the candidate's own-test fields (FEAT-29). */
export function inCodeWorkArea(e: Event): boolean {
  return within(e, WORK_AREA_SELECTOR);
}

/** EF-11: the file-upload answer card marks itself `data-file-drop`; a drop
 * that carries files inside it is an upload, never a paste. */
export const FILE_DROP_ATTR = "data-file-drop";

export function inFileDropZone(e: DragEvent): boolean {
  const files = e.dataTransfer?.files;
  if (!files || files.length === 0) return false;
  return within(e, `[${FILE_DROP_ATTR}]`);
}

function newId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Older Safari (< 15.4) and some embedded browsers lack randomUUID but do
  // have getRandomValues. Build a format-valid v4 UUID by hand.
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
  }
  // Last resort. Not cryptographically random but the backend only needs a
  // valid UUID *format* for the dedup index; collision risk at our scale
  // (events per attempt) is negligible.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Attempt-scoped one-shot guard for `second_screen_detected`. The controller is
// recreated on every candidate route change — the candidate flow's crash
// boundary is keyed by pathname (App.tsx), so `Question` remounts on each
// Next/Back and a plain instance-scoped flag would re-arm and re-emit the event
// every time. Persisting the "already emitted" fact in sessionStorage keyed by
// attemptId makes it survive controller recreation AND full page reload, keeps
// it isolated per attempt, and clears on tab close. Wrapped in try/catch so
// private-mode / disabled storage degrades to the old instance-scoped behavior
// instead of throwing during construction (mirrors loadAttemptId/saveAttemptId).
const SECOND_SCREEN_GUARD_PREFIX = "anticheat:second-screen:";

function readSecondScreenGuard(attemptId: string): boolean {
  try {
    return (
      sessionStorage.getItem(SECOND_SCREEN_GUARD_PREFIX + attemptId) === "1"
    );
  } catch {
    return false;
  }
}

function markSecondScreenGuard(attemptId: string): void {
  try {
    sessionStorage.setItem(SECOND_SCREEN_GUARD_PREFIX + attemptId, "1");
  } catch {
    /* storage unavailable — fall back to the instance-scoped flag */
  }
}

// Attempt-scoped one-shot guard for the ARM-TIME BACKDATE (same pattern as the
// second-screen guard above, same reason: the controller is recreated on every
// candidate navigation). The arming blind spot — "the pointer may already have
// been outside when we attached our listeners" — is real exactly ONCE, at the
// attempt's first controller mount. Every later mount is a mid-attempt route
// change, where "no boundary event yet" almost always means the pointer sat
// INSIDE (the candidate clicked Next) or simply never moved. Without this
// guard each remount re-armed the inference and dispose() (which closes any
// open excursion) fabricated up to CURSOR_ARM_EXCURSION_CAP_MS of phantom
// out_ms per navigation — a keyboard-nav candidate with the pointer parked on
// a second monitor accumulated ~10s × questions and crossed the server's 30s
// cumulative flag with zero mouse movement.
const CURSOR_ARM_GUARD_PREFIX = "anticheat:cursor-armed:";

function readCursorArmGuard(attemptId: string): boolean {
  try {
    return sessionStorage.getItem(CURSOR_ARM_GUARD_PREFIX + attemptId) === "1";
  } catch {
    return false;
  }
}

function markCursorArmGuard(attemptId: string): void {
  try {
    sessionStorage.setItem(CURSOR_ARM_GUARD_PREFIX + attemptId, "1");
  } catch {
    /* storage unavailable — degrade to per-instance arming (pre-guard behavior) */
  }
}

// FEAT-38 — the attempt's cumulative interaction counts, persisted the same
// way as the two guards above and for the same reason: the controller is
// recreated on every candidate navigation, and the counts are per ATTEMPT.
// `emitted` is the last summary sent, so a remount never re-sends an
// unchanged one; `emitted_at` gates the cadence; `touch_sent` marks the
// one-shot "no pointer" summary of a touch-only device; `segment` is this
// TAB's random id — sessionStorage dies with the tab, so a break taken with
// the tab closed or a resume on another device starts the counts at zero
// under a new segment, and the server sums the segments instead of letting
// the fresh tab's smaller counts hide behind the old ones. Counts only —
// nothing in here is a key, a coordinate or an event.
const INTERACTION_STORE_PREFIX = "anticheat:interaction:";
// A changed count goes out at once the first time, then at most this often
// on the flush cadence (dispose and pagehide always send a change): ~120
// rows an hour for a busy candidate instead of one per 2 s tick.
const INTERACTION_SUMMARY_MIN_MS = 30_000;

interface InteractionStore {
  counts: InteractionCounts;
  emitted: InteractionCounts | null;
  emitted_at: number;
  touch_sent?: boolean;
  segment?: string;
}

const SEGMENT_RE = /^[A-Za-z0-9-]{1,64}$/;

function readInteractionStore(attemptId: string): InteractionStore | null {
  try {
    const raw = sessionStorage.getItem(INTERACTION_STORE_PREFIX + attemptId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<InteractionStore> | null;
    if (!parsed || typeof parsed !== "object") return null;
    return {
      counts: maxCounts(parsed.counts, null),
      emitted: parsed.emitted ? maxCounts(parsed.emitted, null) : null,
      emitted_at:
        typeof parsed.emitted_at === "number" && Number.isFinite(parsed.emitted_at)
          ? parsed.emitted_at
          : 0,
      touch_sent: parsed.touch_sent === true,
      segment:
        typeof parsed.segment === "string" && SEGMENT_RE.test(parsed.segment)
          ? parsed.segment
          : undefined,
    };
  } catch {
    return null;
  }
}

function writeInteractionStore(attemptId: string, store: InteractionStore): void {
  try {
    sessionStorage.setItem(INTERACTION_STORE_PREFIX + attemptId, JSON.stringify(store));
  } catch {
    /* storage unavailable — the counts live only as long as this controller */
  }
}

/** Second-screen detection capability + current reading.
 *
 * `screen.isExtended` is a prompt-free boolean, but it only exists in Chromium
 * over a secure context — it's `undefined` in Firefox/Safari and over plain
 * `http://<LAN-IP>`. Distinguishing "supported but no second screen" from
 * "this browser physically can't tell us" is what lets the recruiter UI stop
 * showing an ambiguous "—". `value` is meaningful only when supported.
 *
 * BLIND SPOT this probe cannot see: a Permissions-Policy that denies
 * `window-management` makes Chromium report a confident `false` — NOT
 * `undefined` — so a header regression would silently downgrade real
 * "Detected" attempts to "None detected". backend/main.py pins
 * `window-management=(self)` and test_security_headers.py guards the header;
 * don't loosen either without revisiting this.
 */
export function probeSecondScreen(): {
  support: "supported" | "unsupported";
  value: boolean;
} {
  const scr = window.screen as Screen & { isExtended?: boolean };
  if (typeof scr?.isExtended !== "boolean") {
    return { support: "unsupported", value: false };
  }
  return { support: "supported", value: scr.isExtended };
}

/** Fraction (0–1) of the window past the primary screen's bounds, taken as the
 * larger per-axis overhang (0 = fully on primary, 1 = a whole edge past the
 * boundary). Pure (no DOM reads) so it unit-tests without jsdom. `offX`/`offY`
 * sum the left+right (resp. top+bottom) overhang and clamp to the window size,
 * so a window wider/taller than the screen never exceeds 1. Degenerate or
 * non-finite geometry returns 0 (never flags). Per-axis MAX (not area) so a
 * corner drag can't newly flag what the old center test didn't — the new
 * predicate is a strict subset of the old center-crossing one.
 */
export function offscreenFraction(rect: {
  screenX: number;
  screenY: number;
  outerWidth: number;
  outerHeight: number;
  screenWidth: number;
  screenHeight: number;
}): number {
  const { screenX, screenY, outerWidth, outerHeight, screenWidth, screenHeight } =
    rect;
  if (!(outerWidth > 0) || !(outerHeight > 0)) return 0;
  if (!(screenWidth > 0) || !(screenHeight > 0)) return 0;
  if (!Number.isFinite(screenX) || !Number.isFinite(screenY)) return 0;
  const offX =
    Math.min(
      outerWidth,
      Math.max(0, -screenX) + Math.max(0, screenX + outerWidth - screenWidth),
    ) / outerWidth;
  const offY =
    Math.min(
      outerHeight,
      Math.max(0, -screenY) + Math.max(0, screenY + outerHeight - screenHeight),
    ) / outerHeight;
  return Math.max(offX, offY);
}

/** Positional second-screen fallback for browsers without `screen.isExtended`
 * (Firefox, Safari): `window.screenX/screenY` are virtual-desktop coordinates
 * while `screen.width/height` describe a single screen, so a window sitting
 * mostly outside [0, screen.width) × [0, screen.height) cannot be on the primary
 * display — it was dragged to a secondary monitor. Flags only when MORE than
 * `SECOND_SCREEN_OFFSCREEN_FRACTION` of the window is off the primary screen, so
 * a single-monitor window merely nudged past an edge does NOT fake a second
 * display (R1); a genuinely relocated window reads ~1.0. Conservative by
 * construction: a mirrored display, or a second monitor the test window never
 * visits, stays undetected (the capability telemetry already says "unsupported",
 * so absence still reads honestly as "browser can't report").
 * The `document.hidden` gate matters: Windows parks minimized windows at
 * -32000,-32000 (and automation tabs can sit at -25600,-25600 with
 * `document.hidden === true`) — a window nobody can see (fraction ~1.0) must
 * never count as a second screen.
 */
export function windowOnSecondaryScreen(): boolean {
  if (document.hidden) return false;
  return (
    offscreenFraction({
      screenX: window.screenX,
      screenY: window.screenY,
      outerWidth: window.outerWidth || 0,
      outerHeight: window.outerHeight || 0,
      screenWidth: window.screen?.width ?? 0,
      screenHeight: window.screen?.height ?? 0,
    }) > SECOND_SCREEN_OFFSCREEN_FRACTION
  );
}

/** Cursor-excursion tracking capability. mouseleave/mouseenter need a fine
 * pointer: on a touch-only device (tablet/phone with no mouse or trackpad)
 * they can never fire, so the recruiter's "Cursor leaves: 0" would be absence
 * of capability dressed up as a clean reading. `any-pointer: fine` is true
 * whenever ANY attached pointer is fine (touch laptops and iPad+trackpad stay
 * "supported"); when no pointer is fine and the device is coarse/touch, report
 * "unsupported". A browser whose matchMedia answers "no pointers at all" (or
 * lacks matchMedia entirely) is treated as a plain desktop — claiming
 * supported there preserves the status quo rather than inventing a caveat.
 */
export function probeCursorTracking(): { support: "supported" | "unsupported" } {
  try {
    if (typeof window.matchMedia === "function") {
      if (window.matchMedia("(any-pointer: fine)").matches) {
        return { support: "supported" };
      }
      const coarse = window.matchMedia("(any-pointer: coarse)").matches;
      const touch = (navigator.maxTouchPoints ?? 0) > 0;
      if (coarse || touch) return { support: "unsupported" };
    }
  } catch {
    /* fall through to the desktop default */
  }
  return { support: "supported" };
}

/**
 * The page-unload send: `sendBeacon` (keepalive fetch where it is missing),
 * cut to the server's batch cap. Fire-and-forget by nature — a beacon never
 * sees its response, so a 429 here is a lost batch.
 */
function beaconEvents(url: string, attemptId: string, events: AntiCheatEvent[]): void {
  for (let i = 0; i < events.length; i += MAX_EVENTS_PER_REQUEST) {
    // attempt_id goes in the BODY (not a query param) so the UUID never lands
    // in proxy/access logs; sendBeacon can't set the X-Attempt-Id header (F2).
    const body = JSON.stringify({
      events: events.slice(i, i + MAX_EVENTS_PER_REQUEST),
      attempt_id: attemptId,
    });
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      continue;
    }
    // Fallback for browsers without sendBeacon. Modern fetch supports the
    // keepalive flag, which lets the request survive page unload.
    try {
      void fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    } catch {
      // Page is unloading; nothing more we can do.
    }
  }
}

// A Next/Back remounts Question (App keys the route by pathname), so the
// controller is disposed and re-created on every question. Beaconing the queue
// on each dispose was one POST per question: a sub-second skim outran /events'
// 120/min and a beacon can neither see its 429 nor retry, so those events were
// lost. `dispose()` PARKS the queue here instead and the next controller of the
// same attempt adopts it — one flush cadence for the attempt however fast it
// moves. Nobody adopting within HANDOFF_MS (Review, Done, an error page) sends
// it then; a page unload in between sends it at once.
let parked: {
  url: string;
  attemptId: string;
  events: AntiCheatEvent[];
  timer: number;
} | null = null;

/** Empty the slot and unwire it — ONE teardown for both ways out, so neither
 *  can forget the listener; the caller decides what the events become. */
function unpark(): NonNullable<typeof parked> | null {
  const p = parked;
  if (!p) return null;
  parked = null;
  window.clearTimeout(p.timer);
  window.removeEventListener("pagehide", releaseParkedEvents);
  return p;
}

/** Send whatever a disposed controller parked, now (also the unload path). */
export function releaseParkedEvents(): void {
  const p = unpark();
  if (p) beaconEvents(p.url, p.attemptId, p.events);
}

function parkEvents(url: string, attemptId: string, events: AntiCheatEvent[]): void {
  if (events.length === 0) return;
  if (parked && (parked.url !== url || parked.attemptId !== attemptId)) {
    releaseParkedEvents();
  }
  if (parked) {
    parked.events = parked.events.concat(events);
    window.clearTimeout(parked.timer);
  } else {
    parked = { url, attemptId, events, timer: 0 };
    window.addEventListener("pagehide", releaseParkedEvents);
  }
  parked.timer = window.setTimeout(releaseParkedEvents, HANDOFF_MS);
}

function adoptParkedEvents(url: string, attemptId: string): AntiCheatEvent[] {
  if (!parked || parked.url !== url || parked.attemptId !== attemptId) return [];
  return unpark()?.events ?? [];
}

export function createAntiCheat(opts: AntiCheatOptions): AntiCheatController {
  const flushUrl = opts.flushUrl ?? `/api/t/${opts.token}/events`;
  const batchMs = opts.batchMs ?? DEFAULT_BATCH_MS;
  const detectors = opts.detectors !== false;
  // FEAT-38: the fold runs only under activity monitoring AND where /preview
  // said the context is available for this attempt.
  const interactionOn = detectors && opts.interaction === true;

  // What the previous controller of this attempt left unsent (a remount).
  let queue: AntiCheatEvent[] = adoptParkedEvents(flushUrl, opts.attemptId);
  let disposed = false;
  let lastBlurAt: number | null = null;
  let lastVisibilityAt = 0;
  let offlineAt: number | null = null;
  let lastQuestionPosition: number | null = null;
  // B2 cursor tracking: timestamp the pointer left the viewport; whether a
  // tab-switch (blur / visibility-hide) happened during that excursion (so we
  // don't double-count it as a distinct cursor signal); whether we've already
  // emitted the one-shot second-screen event (seeded from the attempt-scoped
  // sessionStorage guard so a controller remount doesn't re-emit it).
  let cursorOutAt: number | null = null;
  let blurredWhileCursorOut = false;
  // FEAT-25: with the detectors off, start as "already emitted" so neither
  // the mount-time check nor the poll below can report a second screen.
  let secondScreenEmitted = !detectors || readSecondScreenGuard(opts.attemptId);
  // B2 arming blind spot: the controller only mounts at Question 1, so if the
  // pointer is ALREADY off the viewport at that moment (candidate flung the
  // mouse to a second monitor right after "Begin assessment"), mouseleave fired
  // before our listeners attached and the excursion is invisible. `armedAt`
  // stamps mount time; `sawBoundary` records whether any mouseleave/mouseenter
  // has fired yet. If the first-ever boundary event is a mouseenter, DOM
  // semantics guarantee the pointer was outside since arm-time (an inside
  // pointer would fire mouseleave first), so we backdate the excursion to
  // armedAt — no synchronous "is the pointer inside?" query needed.
  //
  // ONE-SHOT: only the attempt's FIRST controller mount may run that inference
  // (see readCursorArmGuard). Seeding `sawBoundary = true` on every later
  // mount disables ONLY the backdate — real mouseleave/mouseenter excursions
  // still measure normally on every mount.
  const armedAt = Date.now();
  //
  // FEAT-25: with the detectors off no boundary listener exists, so the
  // backdate must be disabled too — `dispose()` and `onPageHide` still call
  // `emitCursorReturn`, which would otherwise invent an excursion as long as
  // the controller lived. The one-shot guard is left unconsumed.
  let sawBoundary = !detectors || readCursorArmGuard(opts.attemptId);
  if (detectors) markCursorArmGuard(opts.attemptId);
  // A2 dedupe for the blind spot: a tab-switch (blur/visibilitychange) can fire
  // while the pointer is still outside-since-arm — BEFORE any boundary event
  // opens `cursorOutAt` — so the `cursorOutAt !== null` dedupe guard misses it
  // and the eventual backdated excursion double-counts the same physical
  // "moved away" as both tab_blur AND cursor_left_window. Record any pre-boundary
  // blur; the backdate branch (which only fires when the pointer WAS outside
  // since arm) folds it into the tab-switch dedupe.
  let blurredBeforeBoundary = false;
  // Set when the server returns 429: skip flushes until this epoch ms.
  // Events stay queued and ship in the deferred flush.
  let nextFlushNotBefore = 0;

  // EF-11 file-picker window (see beginFilePicker below).
  let pickerActive = false;
  let pickerEndTimer: number | null = null;
  let pickerCapTimer: number | null = null;

  const emit = (payload: AntiCheatPayload): void => {
    if (disposed) return;
    if (pickerActive && FILE_PICKER_SUPPRESSED.has(payload.type)) return;
    queue.push({
      client_event_id: newId(),
      payload,
      client_t: new Date().toISOString(),
      question_id: opts.getQuestionId?.(),
    });
    try {
      opts.onEvent?.(payload);
    } catch {
      /* an observer error must never cost the recruiter an event */
    }
  };

  const deactivatePicker = (): void => {
    if (pickerEndTimer !== null) {
      window.clearTimeout(pickerEndTimer);
      pickerEndTimer = null;
    }
    if (pickerCapTimer !== null) {
      window.clearTimeout(pickerCapTimer);
      pickerCapTimer = null;
    }
    if (!pickerActive) return;
    pickerActive = false;
    // Forget anything the dialog left half-open, so a blur that was dropped
    // can never pair with a focus that is recorded (or the reverse).
    lastBlurAt = null;
    cursorOutAt = null;
    blurredWhileCursorOut = false;
  };

  const schedulePickerEnd = (): void => {
    if (!pickerActive || pickerEndTimer !== null) return;
    pickerEndTimer = window.setTimeout(deactivatePicker, FILE_PICKER_GRACE_MS);
  };

  const beginFilePicker = (position?: number): (() => void) => {
    if (disposed) return () => {};
    if (!pickerActive) {
      pickerActive = true;
      emit({ type: "file_picker_opened", position });
      pickerCapTimer = window.setTimeout(deactivatePicker, FILE_PICKER_MAX_MS);
    }
    return schedulePickerEnd;
  };

  // FEAT-38 — the interaction fold. Seeded from the attempt's stored counts
  // so a Next/Back remount continues rather than restarts (the second-screen
  // guard's reason, same storage); pauses are counted within a mount, so the
  // page load between two questions is never one. A touch-only device has
  // no pointer to count: it says so ONCE per attempt and installs nothing.
  let interaction: InteractionFold | null = null;
  let interactionEmitted: InteractionCounts | null = null;
  let interactionEmittedAt = 0;
  let interactionSegment = "";
  if (interactionOn) {
    const stored = readInteractionStore(opts.attemptId);
    if (probeFormFactor() === "touch") {
      if (!stored?.touch_sent) {
        writeInteractionStore(opts.attemptId, {
          counts: { ...ZERO_COUNTS },
          emitted: null,
          emitted_at: Date.now(),
          touch_sent: true,
        });
        emit({ type: "interaction_summary", pointer_supported: false });
      }
    } else {
      interaction = createInteractionFold(stored?.counts ?? null);
      interactionEmitted = stored?.emitted ?? null;
      interactionEmittedAt = stored?.emitted_at ?? 0;
      interactionSegment = stored?.segment ?? newId();
    }
  }

  // ONE summary when the counts changed: at once the first time, then at
  // most every INTERACTION_SUMMARY_MIN_MS on the cadence; `force` (dispose,
  // pagehide) skips the gate but still sends nothing unchanged. The payload
  // is the CUMULATIVE counts: the server folds the max of every summary, so
  // a re-sent or reordered one can never double-count.
  const interactionSummary = (force: boolean): void => {
    if (!interaction) return;
    const counts = interaction.counts();
    if (
      interactionEmitted
        ? equalCounts(counts, interactionEmitted)
        : isZeroCounts(counts)
    ) {
      return;
    }
    const now = Date.now();
    if (!force && now - interactionEmittedAt < INTERACTION_SUMMARY_MIN_MS) return;
    interactionEmitted = counts;
    interactionEmittedAt = now;
    writeInteractionStore(opts.attemptId, {
      counts,
      emitted: counts,
      emitted_at: now,
      segment: interactionSegment,
    });
    emit({
      type: "interaction_summary",
      pointer_supported: true,
      ...counts,
      segment: interactionSegment,
    });
  };

  // Only the mouse: a touch tap or a pen stroke has no "travel" and would
  // read as a warp. The coordinates go into the fold and nowhere else; the
  // keydown is timed, never read.
  const mousePointer = (e: PointerEvent): boolean =>
    e.pointerType === "mouse" || e.pointerType === "" || e.pointerType === undefined;
  const onInteractionMove = (e: PointerEvent): void => {
    if (mousePointer(e)) {
      interaction?.push({ type: "move", t: Date.now(), x: e.clientX, y: e.clientY });
    }
  };
  const onInteractionDown = (e: PointerEvent): void => {
    if (mousePointer(e)) {
      interaction?.push({ type: "down", t: Date.now(), x: e.clientX, y: e.clientY });
    }
  };
  const onInteractionUp = (e: PointerEvent): void => {
    if (mousePointer(e)) interaction?.push({ type: "up", t: Date.now() });
  };
  const onInteractionKey = (): void => {
    interaction?.push({ type: "key", t: Date.now() });
  };
  const onInteractionWheel = (): void => {
    interaction?.push({ type: "wheel", t: Date.now() });
  };

  // A failed send goes back to the front of the queue — or, when the page
  // moved on while it was in flight, to the next controller (`parkEvents`).
  // The server's (attempt_id, client_event_id) unique constraint dedupes any
  // accidental double-send.
  const requeue = (batch: AntiCheatEvent[]): void => {
    queue = batch.concat(queue);
    if (disposed) {
      parkEvents(flushUrl, opts.attemptId, queue);
      queue = [];
    }
  };

  const flush = async (): Promise<void> => {
    // In batches of the server's cap: a remount hand-off can hold more.
    while (queue.length > 0) {
      if (Date.now() < nextFlushNotBefore) return;
      const batch = queue.slice(0, MAX_EVENTS_PER_REQUEST);
      queue = queue.slice(MAX_EVENTS_PER_REQUEST);
      try {
        const res = await fetch(flushUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Attempt-Id": opts.attemptId,
          },
          body: JSON.stringify({ events: batch }),
          keepalive: true,
        });
        if (res.ok) continue;
        // Rate-limited: honor Retry-After and pause flushes until the
        // window resets. Without this, the generic re-queue branch below
        // would hot-loop the 2s timer against the limiter forever.
        if (res.status === 429) {
          const retryAfter = parseInt(res.headers.get("Retry-After") ?? "5", 10);
          const waitMs = (Number.isFinite(retryAfter) ? retryAfter : 5) * 1000;
          nextFlushNotBefore = Date.now() + waitMs;
          requeue(batch);
          return;
        }
        // 409/410 means the attempt was submitted/expired — stop emitting.
        // 404 (10-07 S11): the server no longer knows this attempt for this
        // token (voided by an invitation or a retake, or erased) — it never
        // comes back, so re-queuing would re-post every 2 s until the tab closes.
        if (res.status === 404 || res.status === 409 || res.status === 410) {
          disposed = true;
          return;
        }
        // Otherwise: re-queue at the front for the next flush.
        requeue(batch);
        return;
      } catch {
        requeue(batch);
        return;
      }
    }
  };

  const flushBeacon = (): void => {
    if (queue.length === 0) return;
    const batch = queue;
    queue = [];
    beaconEvents(flushUrl, opts.attemptId, batch);
  };

  // Listeners — wired here, unwired in dispose().

  const onVisibility = (): void => {
    const now = Date.now();
    if (document.hidden) {
      // A tab-switch during a cursor excursion is the same physical action —
      // count it here, suppress the cursor event on return (B2 dedupe).
      if (cursorOutAt !== null) blurredWhileCursorOut = true;
      // …and if it happened before any boundary event (pointer possibly
      // outside-since-arm), remember it for the backdated-excursion dedupe (A2).
      if (!sawBoundary) blurredBeforeBoundary = true;
      // One physical tab-switch fires BOTH `blur` and `visibilitychange`, and
      // the order is not the same across browsers. `onWindowBlur` guards
      // against a preceding visibilitychange; this branch must be symmetric or
      // the pair double-counts wherever `blur` lands first — the recruiter's
      // "Tab switches: 4" then really means 2. `lastBlurAt !== null` is the
      // same "already counted as away" test onWindowBlur uses; the dedupe
      // flags above are still set either way, because they are about the
      // physical action, not about which listener reported it.
      const alreadyCountedAway = lastBlurAt !== null;
      lastBlurAt = now;
      lastVisibilityAt = now;
      if (!alreadyCountedAway) emit({ type: "tab_blur" });
      flushBeacon();
    } else {
      lastVisibilityAt = now;
      // Same guard as onWindowFocus: a return with no recorded leave is not a
      // return. EF-11 — a file dialog left open past the 120 s cap hid the page
      // inside the picker window (blur dropped) and the cap forgot it, so
      // without this the cancel logged a lone tab_focus (away_ms 0).
      if (lastBlurAt === null) return;
      emit({ type: "tab_focus", away_ms: now - lastBlurAt });
      lastBlurAt = null;
    }
  };

  const onWindowBlur = (): void => {
    const now = Date.now();
    // Mark the dedupe before any early-return: a blur while the cursor is out is
    // a tab-switch, even when the tab_blur itself is deduped by visibility.
    if (cursorOutAt !== null) blurredWhileCursorOut = true;
    if (!sawBoundary) blurredBeforeBoundary = true; // pre-boundary dedupe (A2)
    if (now - lastVisibilityAt < VISIBILITY_DEDUPE_MS) return;
    if (lastBlurAt !== null) return;
    lastBlurAt = now;
    emit({ type: "tab_blur" });
  };

  const onWindowFocus = (): void => {
    const now = Date.now();
    // EF-11: the dialog closed (or the candidate came back) — end the picker
    // window shortly, whatever the input reports.
    if (pickerActive) schedulePickerEnd();
    if (now - lastVisibilityAt < VISIBILITY_DEDUPE_MS) return;
    if (lastBlurAt === null) return;
    emit({ type: "tab_focus", away_ms: now - lastBlurAt });
    lastBlurAt = null;
  };

  // Shared by the clipboard and drag-and-drop paths so both are counted
  // identically. Emitting the SAME `paste` event for a drop is deliberate: the
  // server's risk fold, the exports and the webhook all read `paste` already,
  // so closing the drop bypass needs no schema change downstream. `source`
  // only lets the recruiter timeline say which it was.
  const recordPastedText = (
    e: Event,
    text: string,
    source: "clipboard" | "drop",
  ): void => {
    // FEAT-29: the own-test fields count as the editor here.
    const editor = inCodeWorkArea(e);
    const payload: PastePayload = {
      type: "paste",
      length: text.length,
      in_editor: editor,
      source,
    };
    emit(payload);
    if (!editor) e.preventDefault();
    // Notify the UI on a MACROTASK — never synchronously, never on a
    // microtask. `paste`/`drop` are React *discrete* events, so a setState
    // made from this capture-phase listener is flushed in a microtask, and the
    // browser runs a microtask checkpoint after EVERY listener returns — i.e.
    // before the event reaches Monaco's own paste handler. A synchronously
    // mounted modal would steal focus mid-dispatch. setTimeout(0) runs only
    // after the whole dispatch (and Monaco's insert) has completed, so "the
    // paste has landed before the notice appears" is a guarantee, not luck.
    // The dispose guard stops a Next/Back remount firing a stale call.
    if (opts.onPasteRecorded) {
      window.setTimeout(() => {
        if (disposed) return;
        try {
          opts.onPasteRecorded?.(payload);
        } catch {
          /* the notice is cosmetic — recording already happened above */
        }
      }, 0);
    }
  };

  const onPaste = (e: ClipboardEvent): void => {
    // Fires in the capture phase so Monaco can't swallow it before we count it.
    // Pasting external text into the code editor is the strongest cheat signal,
    // so we capture every paste regardless of where it lands; we still allow
    // the paste to proceed inside the editor so candidates can move code around.
    recordPastedText(e, e.clipboardData?.getData("text/plain") ?? "", "clipboard");
  };

  // Drag-and-drop bypassed the clipboard entirely until 2026-08-24: dragging a
  // selection from another window (or a .txt file) straight into Monaco landed
  // the text without firing `paste`, so the single strongest cheat signal was
  // fully evadable in every browser. `dragover` must be prevented for `drop`
  // to fire at all on a non-editable target, which is also what stops a dropped
  // file from navigating the page away.
  const onDragOver = (e: DragEvent): void => {
    if (!inCodeWorkArea(e)) e.preventDefault(); // FEAT-29: + the own-test fields
  };

  const onDrop = (e: DragEvent): void => {
    // EF-11 (2026-10-15): a FILE dropped on a file-upload answer card is the
    // candidate's answer, not a paste — the card (`[data-file-drop]`) takes it
    // through the same checks as its button. Everything else — a text
    // selection anywhere, a file dropped outside the card — is recorded and
    // blocked exactly as before.
    if (inFileDropZone(e)) return;
    recordPastedText(e, e.dataTransfer?.getData("text/plain") ?? "", "drop");
  };

  const onKeyDown = (e: KeyboardEvent): void => {
    // Matching lives in ./shortcuts.ts and consults the PHYSICAL key as well as
    // the produced character: on macOS, Option mangles the character (Option+I
    // -> "ˆ"), so a key-only matcher never fired there. See that module.
    const chord = matchBlockedShortcut(describeKeyEvent(e));
    if (!chord) return;
    // FEAT-29: copy / cut / paste work in the own-test fields as in the editor.
    if (inCodeWorkArea(e) && chord.editorAllowed) return;
    emit({ type: "keyboard_shortcut_blocked", shortcut: chord.name });
    e.preventDefault();
  };

  const onOffline = (): void => {
    offlineAt = Date.now();
    emit({ type: "network_offline" });
  };

  const onOnline = (): void => {
    const offlineMs = offlineAt !== null ? Date.now() - offlineAt : 0;
    offlineAt = null;
    emit({ type: "network_online", offline_ms: offlineMs });
  };

  // B2 — cursor leaving the viewport. mouseleave/mouseenter on the root element
  // fire ONCE on a true page exit/entry (they don't bubble), so this is cheap
  // and matches the MDN reference Emilia sent. We time the excursion and emit
  // only sustained ones that weren't already a tab-switch (a click on the other
  // screen fires blur/visibilitychange and is counted there — see the dedupe).
  const onDocMouseLeave = (): void => {
    sawBoundary = true;
    if (cursorOutAt !== null) return; // already out
    cursorOutAt = Date.now();
    blurredWhileCursorOut = false;
  };

  const emitCursorReturn = (): void => {
    // Arming blind spot: a mouseenter with no open excursion, and no boundary
    // event seen yet, means the pointer was outside since the controller armed.
    // Backdate the excursion to armedAt so the "read from a second monitor from
    // the very start" case isn't lost. Any real mouseleave would have set
    // sawBoundary first, so this never fires for a pointer that started inside.
    const backdated = cursorOutAt === null && !sawBoundary;
    if (backdated) {
      cursorOutAt = armedAt;
      // A2: a tab-switch during the outside-since-arm window already counted
      // this physical "moved away" as tab_blur — dedupe the backdated excursion.
      if (blurredBeforeBoundary) blurredWhileCursorOut = true;
    }
    sawBoundary = true;
    if (cursorOutAt === null) return;
    let outMs = Date.now() - cursorOutAt;
    cursorOutAt = null;
    if (blurredWhileCursorOut) return; // deduped: already a tab-switch
    if (outMs < CURSOR_OUT_MIN_MS) return; // benign brief reach
    // A1: bound the ambiguous arm-time excursion so a resting dual-monitor
    // pointer can't single-handedly cross the cumulative flag threshold.
    if (backdated) outMs = Math.min(outMs, CURSOR_ARM_EXCURSION_CAP_MS);
    emit({ type: "cursor_left_window", out_ms: outMs });
  };

  // B2 — a second display connected (screen.isExtended). Prompt-free boolean,
  // Chromium-only. Where it's undefined (Firefox/Safari) the positional
  // fallback speaks instead: a window dragged onto a secondary monitor betrays
  // itself through its own screen coordinates. isExtended stays authoritative
  // when present — a half-offscreen window on a single Chromium screen must
  // not fake a second display. Emit once on first detection (either path).
  // The unsupported/none-detected cases are reported separately, on
  // attempt_started (see Gate.tsx), so the recruiter can tell them apart.
  const checkSecondScreen = (): void => {
    if (secondScreenEmitted) return;
    const { support, value } = probeSecondScreen();
    // Indirect placement check (./displays.ts) — reads which DISPLAY the window
    // is on from `screen.availLeft/availTop`, which every browser implements.
    // This is the Firefox/Safari path, and it is exact where the geometry
    // heuristic below is only a proxy.
    const placement = probeDisplayPlacement();
    const detected =
      support === "supported"
        ? // Chromium. `isExtended` stays authoritative, but OR in the placement
          // check to close the blind spot documented on probeSecondScreen():
          // a Permissions-Policy denying `window-management` makes Chromium
          // return a CONFIDENT `false`, silently downgrading a real detection.
          // Safe to OR because displayPlacement() is exact — unlike
          // offscreenFraction(), it cannot fire for a half-offscreen window on
          // a single screen.
          value || placement === "non_primary"
        : // No display API. Take either indirect signal. These are ORed rather
          // than laddered deliberately: the cross-display reading of
          // `availLeft` is documented (MDN: "relative to the OS virtual screen
          // arrangement") but could not be verified on real Firefox hardware
          // when this shipped, so the previously-shipped geometry heuristic is
          // KEPT as a parallel path. If availLeft turns out to be virtual
          // everywhere, this is strictly more detection; if some browser reports
          // 0 always, behaviour there is exactly what it was before. Either way
          // there is no regression. See docs/operations/TEST.md.
          placement === "non_primary" || windowOnSecondaryScreen();
    if (detected) {
      secondScreenEmitted = true;
      // Persist before emitting so a controller recreated by a route remount
      // seeds `secondScreenEmitted = true` and never re-emits for this attempt.
      markSecondScreenGuard(opts.attemptId);
      emit({ type: "second_screen_detected" });
    }
  };

  const onPageHide = (): void => {
    // The camera's open no-face stretch first (see AntiCheatOptions).
    try {
      opts.onPageHide?.();
    } catch {
      /* an observer error must never cost the final beacon */
    }
    // Close a still-open excursion before the final beacon, mirroring
    // dispose(): without this, a pointer parked outside at tab-close lost its
    // whole last excursion (the mouseenter that would have emitted it never
    // comes). The A2 pre-boundary blur dedupe still applies — on a real tab
    // close, visibilitychange(hidden) fires first and suppresses a backdated
    // phantom, so only genuinely-measured time ships.
    emitCursorReturn();
    interactionSummary(true); // FEAT-38: the last counts ride the final beacon
    flushBeacon();
  };

  // Always on, detectors or not: `focus` ends the file-picker window (with no
  // blur listener `lastBlurAt` stays null, so it emits nothing), and
  // `pagehide` sends the final beacon.
  window.addEventListener("focus", onWindowFocus);
  window.addEventListener("pagehide", onPageHide);
  if (detectors) {
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onWindowBlur);
    // Paste uses capture phase so Monaco's own textarea listener can't
    // stopPropagation before the document-level handler runs.
    document.addEventListener("paste", onPaste, true);
    // Capture phase for the same reason as paste: Monaco must not be able to
    // swallow the drop before it is counted.
    document.addEventListener("dragover", onDragOver, true);
    document.addEventListener("drop", onDrop, true);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    // mouseleave/mouseenter on <html> — one clean pair per viewport exit/entry.
    document.documentElement.addEventListener("mouseleave", onDocMouseLeave);
    // `emitCursorReturn` is a stable reference and ignores the event arg, so it
    // registers directly — no passthrough wrapper needed.
    document.documentElement.addEventListener("mouseenter", emitCursorReturn);
    if (interaction) {
      // FEAT-38 — capture + passive: Monaco must not be able to swallow a
      // keydown before it is timed, and the fold never preventDefaults.
      const opt = { capture: true, passive: true } as const;
      document.addEventListener("pointermove", onInteractionMove, opt);
      document.addEventListener("pointerdown", onInteractionDown, opt);
      document.addEventListener("pointerup", onInteractionUp, opt);
      document.addEventListener("keydown", onInteractionKey, opt);
      document.addEventListener("wheel", onInteractionWheel, opt);
    }
  }

  // Check the second screen immediately, then poll it on the flush cadence (a
  // cheap boolean read) so a monitor plugged in mid-test is still caught.
  checkSecondScreen();
  const intervalId = window.setInterval(() => {
    checkSecondScreen();
    interactionSummary(false); // FEAT-38: gated, only on a change
    void flush();
  }, batchMs);

  return {
    dispose(): void {
      if (disposed) return;
      // Flush a still-open cursor excursion while emit() is still live (before
      // disposed=true no-ops it), mirroring the camera monitor's teardown flush.
      emitCursorReturn();
      interactionSummary(true); // FEAT-38: while emit() is still live
      disposed = true;
      deactivatePicker();
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onWindowBlur);
      window.removeEventListener("focus", onWindowFocus);
      document.removeEventListener("paste", onPaste, true);
      document.removeEventListener("dragover", onDragOver, true);
      document.removeEventListener("drop", onDrop, true);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("pagehide", onPageHide);
      document.documentElement.removeEventListener("mouseleave", onDocMouseLeave);
      document.documentElement.removeEventListener("mouseenter", emitCursorReturn);
      document.removeEventListener("pointermove", onInteractionMove, true);
      document.removeEventListener("pointerdown", onInteractionDown, true);
      document.removeEventListener("pointerup", onInteractionUp, true);
      document.removeEventListener("keydown", onInteractionKey, true);
      document.removeEventListener("wheel", onInteractionWheel, true);
      // Not a beacon per question: the next controller ships it (see `parked`).
      parkEvents(flushUrl, opts.attemptId, queue);
      queue = [];
    },
    flushNow: flush,
    beginFilePicker,
    isFilePickerActive: () => pickerActive,
    onQuestionChange(position: number): void {
      if (position === lastQuestionPosition) return;
      lastQuestionPosition = position;
      emit({ type: "question_viewed", position });
    },
    emit,
  };
}
